import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import ReactPlayer from 'react-player';
import { observer } from 'mobx-react-lite';

import { getColor } from '../common/UICommon';
import { ContainerBox } from '../styledComponent/Boxes.style';

import { BaseStep, StepProps, StepWizardProps, TitleAndDescriptionProps } from '../common/BaseStep';
import { PointList } from '../common/PointList';
import { SecondaryTitleDescription } from '../common/SecondaryTitleDescription';
import { CarouselComponent, CarouselType } from '../common/CarouselComponent';
import { FacilitatorSection } from '../common/FacilitatorSection';
import { StatisticSection, StatisticSectionProps } from '../common/StatisticSection';
import { QuoteCardProps } from '../common/QuoteCard';
import { QuestionAndAnswer } from '../common/QuestionAndAnswer';
import { RecommenderPlayIcon } from '../common/RecommenderPlayIcon';

import verWellAward from '../../assets/verWellAward.webp';

const RecommenderDiv = styled.div`
  font-size: 14px;
  font-weight: 300;
  color: ${getColor('BLACK_60')};
  line-height: 20px;
  text-align: center;
  margin-bottom: 32px;
  padding-bottom: 32px;
  border-bottom: 1px solid ${getColor('BLACK_10')};
`;
const RecommenderTitle = styled.p`
  color: ${getColor('BLACK')};
  font-weight: 500;
  padding-top: 16px;
`;

const Img = styled.img`
  border-radius: 50%;
  width: 200px;
  height: 200px;
  margin: 32px auto;
  display: block;
`;

const Player = styled(ReactPlayer)`
  iframe {
    border-radius: 16px;
  }
`;

const MarginTop = styled.div`
  margin-top: 22px;
`;

export interface QuestionAndAnswerProp {
  question: string;
  answer: string;
}

interface ScienceSectionProps extends TitleAndDescriptionProps {
  recommenderSection: {
    videoUrl?: string;
    imageUrl: string;
    quote: string;
    recommenderName: string;
    recommenderJob: string;
  };
}

export interface Facilitator {
  name: string;
  imageLink: string;
  bio: string;
}

interface FacilitatorSectionProps extends TitleAndDescriptionProps {
  facilitatorsList: Facilitator[];
}

interface ProductInformationStepProps extends TitleAndDescriptionProps, StepProps, StepWizardProps {
  mainPoints: string[];
  usersQuotes: QuoteCardProps[];
  statisticInformation: StatisticSectionProps;
  facilitatorSection: FacilitatorSectionProps;
  scienceSection: ScienceSectionProps;
  questionsList: QuestionAndAnswerProp[];
  ourMethod: TitleAndDescriptionProps;
}

export const ProductInformationStep = observer((props: ProductInformationStepProps) => {
  const {
    mainPoints,
    statisticInformation,
    facilitatorSection,
    scienceSection,
    questionsList,
    ourMethod,
    usersQuotes,
    isActive,
    stepName,
  } = props;

  useEffect(() => {
    if (isActive) return window.scrollTo(0, 0);
  }, [isActive]);

  return (
    <>
      {isActive ? (
        <BaseStep {...props}>
          <>
            <ContainerBox>
              <PointList pointList={mainPoints} />
            </ContainerBox>

            <ContainerBox>
              <StatisticSection
                percentageNumber={statisticInformation.percentageNumber}
                description={statisticInformation.description}
              />
            </ContainerBox>
            {/* need information for this */}
            {usersQuotes ? (
              <CarouselComponent
                stepName={stepName}
                type={CarouselType.Quote}
                listInformation={usersQuotes}
                isBottomBorder={true}
              />
            ) : (
              ''
            )}

            {facilitatorSection ? (
              <>
                <ContainerBox>
                  <SecondaryTitleDescription {...facilitatorSection} />
                </ContainerBox>

                <FacilitatorSection facilitatorList={facilitatorSection.facilitatorsList} isBottomBorder={true} />
              </>
            ) : (
              ''
            )}

            {scienceSection ? (
              <ContainerBox>
                <SecondaryTitleDescription {...scienceSection} />

                {scienceSection.recommenderSection.videoUrl && scienceSection.recommenderSection.videoUrl.length > 0 ? (
                  <>
                    <Player
                      url={scienceSection.recommenderSection.videoUrl}
                      controls={true}
                      width={'311px'}
                      height={'366px'}
                      className="react-player"
                      playIcon={<RecommenderPlayIcon />}
                    />

                    <RecommenderDiv>
                      <RecommenderTitle>{scienceSection.recommenderSection.recommenderName}</RecommenderTitle>
                      <p>{scienceSection.recommenderSection.recommenderJob}</p>
                    </RecommenderDiv>
                  </>
                ) : (
                  ''
                )}
              </ContainerBox>
            ) : (
              ''
            )}

            {questionsList ? (
              <ContainerBox>
                <SecondaryTitleDescription title="Frequently Asked Questions" />

                {questionsList.map((questionAndAnswer) => (
                  <QuestionAndAnswer key={questionAndAnswer.question} questionAndAnswer={questionAndAnswer} />
                ))}

                <MarginTop />
              </ContainerBox>
            ) : (
              ''
            )}

            {ourMethod ? (
              <ContainerBox>
                <SecondaryTitleDescription {...ourMethod} />

                <Img src={verWellAward} />
              </ContainerBox>
            ) : (
              ''
            )}
          </>
        </BaseStep>
      ) : (
        ''
      )}
    </>
  );
});
